import React from 'react'
import { InView } from 'react-intersection-observer'
import { GuildAccessPill } from 'src/molecules/GuildAccessPill'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import {
  DiscoverItemInteraction,
  IMPRESSION_VISIBILITY_THRESHOLD,
  useCardInteractions,
} from '@/services/Analytics/ContentInteractions'
import { Episode, Project } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import { useSafeTrack } from '@/utils/analytics'

export interface TopPicksLinkViewModel extends LinkViewModel {
  eyebrow?: string
  subtitle?: string
  watchableAt?: string | null
  __typename?: string | null
  attribution?: unknown
}

export interface TopPickCardProps {
  contentLink: TopPicksLinkViewModel
  itemIndex: number
  isGuildMember: boolean
  loading: boolean
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (contentLink: TopPicksLinkViewModel, itemIndex: number) => DiscoverItemInteraction | null
}

export const TopPickCard: React.FC<TopPickCardProps> = ({
  contentLink,
  itemIndex,
  isGuildMember,
  loading,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
}) => {
  const track = useSafeTrack()
  const metadata = contentLink.metadata as { episode?: Episode; project?: Project }
  const episode = metadata?.episode
  const contentId = contentLink.id ?? ''

  const onClickTrack = React.useCallback(() => {
    if (contentLink.track) {
      track(contentLink.track.eventName, contentLink.track.payload)
    }
  }, [track, contentLink.track])

  const { handleMouseEnter, handleMouseLeave, handleClick, handleInViewChange } = useCardInteractions({
    contentId,
    item: contentLink,
    itemIndex,
    hoverStartTimeRef,
    impressionTimeoutRef,
    hasTrackedImpression,
    createInteraction,
    onClickTrack,
  })

  return (
    <React.Fragment key={contentLink.imageUrl}>
      <InView onChange={handleInViewChange} threshold={IMPRESSION_VISIBILITY_THRESHOLD}>
        <LandscapeTileCard
          className="mb-2 mr-4"
          linkModel={contentLink}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="absolute inset-0">
            <GuildAccessPill
              isGuildMember={isGuildMember}
              earlyAccessDate={contentLink?.watchableAt ? new Date(contentLink.watchableAt) : undefined}
            />
            {episode?.earlyAccessDate && (
              <GuildAccessNotch
                earlyAccessDate={new Date(episode.earlyAccessDate)}
                publiclyAvailableDate={
                  episode.publiclyAvailableDate ? new Date(episode.publiclyAvailableDate) : undefined
                }
                isGuildMember={isGuildMember}
                loading={loading}
              />
            )}
          </div>
          {!episode?.unavailableReason && <PlayButtonThumbnailOverlay />}
        </LandscapeTileCard>
        <TitleAndSubtitleDisplay className="mr-4" title={contentLink.eyebrow} subtitle={contentLink.subtitle} />
      </InView>
    </React.Fragment>
  )
}
